import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Model from '@ember-data/model';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { storage, StorageKey } from 'uplisting-frontend/utils/storage/api';
import type { IStorageActionProperties } from 'uplisting-frontend/utils/storage/interfaces';

interface IStorageActionPropertiesData {
  properties: ModelRegistry['property'][];
  propertyTags: ModelRegistry['property-tag'][];
}

const defaultPropertiesConfig: IStorageActionProperties = {
  propertyIds: [],
  propertyTagIds: [],
};

function filterRecordFromStore(record: Model | null): boolean {
  return !!record && !record.isDeleted;
}

/**
 * Migrate all local/session(not used atm)/etc storage uses to use that service instead of direct storage calls
 */
export default class StorageService extends Service {
  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service('repositories/property-tag')
  propertyTagRepository!: Services['repositories/property-tag'];

  /**
   * Used to make local storage set/get reactive. On every set we will toggle that field, which will cause getter to re-run
   * and all dependent getters will also re-run
   */
  @tracked propertyConfigListener = false;
  @tracked propertyPictureListener = false;
  @tracked changeoverListener = false;
  @tracked calendarTasksListener = false;
  @tracked calendarEnquiriesListener = false;
  @tracked bookingAmountListener = false;
  @tracked localePreferenceListener = false;
  @tracked sidebarPinnedListener = false;

  get propertyPictureConfig(): boolean {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.propertyPictureListener;

    return (
      storage.local.get<boolean>(StorageKey.CALENDAR_PICTURE_CONFIG) ?? true
    );
  }

  set propertyPictureConfig(value: boolean) {
    this.propertyPictureListener = !this.propertyPictureListener;

    storage.local.set(StorageKey.CALENDAR_PICTURE_CONFIG, value);
  }

  get changeoverConfig(): boolean {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.changeoverListener;

    return (
      storage.local.get<boolean>(StorageKey.CALENDAR_CHANGEOVER_CONFIG) ?? true
    );
  }

  set changeoverConfig(value: boolean) {
    this.changeoverListener = !this.changeoverListener;

    storage.local.set(StorageKey.CALENDAR_CHANGEOVER_CONFIG, value);
  }

  get calendarTasksConfig(): boolean | null {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.calendarTasksListener;

    return storage.local.get<boolean>(StorageKey.CALENDAR_TASKS_CONFIG);
  }

  set calendarTasksConfig(value: boolean) {
    this.calendarTasksListener = !this.calendarTasksListener;

    storage.local.set(StorageKey.CALENDAR_TASKS_CONFIG, value);
  }

  get calendarEnquiriesConfig(): boolean {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.calendarEnquiriesListener;

    return (
      storage.local.get<boolean>(StorageKey.CALENDAR_ENQUIRIES_CONFIG) ?? false
    );
  }

  set calendarEnquiriesConfig(value: boolean) {
    this.calendarEnquiriesListener = !this.calendarEnquiriesListener;

    storage.local.set(StorageKey.CALENDAR_ENQUIRIES_CONFIG, value);
  }

  get bookingAmountConfig(): boolean {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.bookingAmountListener;

    return (
      storage.local.get<boolean>(StorageKey.CALENDAR_BOOKING_AMOUNT_CONFIG) ??
      true
    );
  }

  set bookingAmountConfig(value: boolean) {
    this.bookingAmountListener = !this.bookingAmountListener;

    storage.local.set(StorageKey.CALENDAR_BOOKING_AMOUNT_CONFIG, value);
  }

  get localePreference(): string | null {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.localePreferenceListener;

    return storage.local.get<string>(StorageKey.LOCALE_PREFERENCE);
  }

  set localePreference(value: string) {
    this.localePreferenceListener = !this.localePreferenceListener;

    storage.local.set(StorageKey.LOCALE_PREFERENCE, value);
  }

  get sidebarPinned(): boolean {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.sidebarPinnedListener;

    return storage.local.get<boolean>(StorageKey.SIDEBAR_PINNED) ?? false;
  }

  set sidebarPinned(value: boolean) {
    this.sidebarPinnedListener = !this.sidebarPinnedListener;

    storage.local.set(StorageKey.SIDEBAR_PINNED, value);
  }

  get propertiesConfig(): IStorageActionProperties {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.propertyConfigListener;

    return (
      storage.local.get<IStorageActionProperties>(
        StorageKey.ACTION_PROPERTIES,
      ) ?? defaultPropertiesConfig
    );
  }

  set propertiesConfig(value: IStorageActionProperties) {
    this.propertyConfigListener = !this.propertyConfigListener;

    storage.local.set(StorageKey.ACTION_PROPERTIES, value);
  }

  get propertiesConfigData(): IStorageActionPropertiesData {
    const { propertiesConfig, propertyRepository, propertyTagRepository } =
      this;

    return {
      properties: propertiesConfig.propertyIds
        .map((id) => (id ? propertyRepository.peekRecord(id) : null))
        .filter(filterRecordFromStore) as ModelRegistry['property'][],

      propertyTags: propertiesConfig.propertyTagIds
        .map((id) => (id ? propertyTagRepository.peekRecord(id) : null))
        .filter(filterRecordFromStore) as ModelRegistry['property-tag'][],
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    storage: StorageService;
  }
}
